/**
 * Route helpers for the per-file editor URL `/files/<id>` and for the `#room=`
 * collab fragment. Collab rooms are ephemeral end-to-end sessions, never file
 * access: a URL carrying `#room=` is a PURE session and the cloud-file
 * subsystem must stay dormant on it (see docs/superpowers/specs/
 * 2026-07-28-collab-cloud-files-model.md).
 */

// Matches the canonical `/files/<id>` editor path, and — for backward
// compatibility with links shared/bookmarked before the rename — the legacy
// `/f/<id>` shape. Same id charset for both. We only ever *generate* the new
// `/files/<id>` shape (see `fileEditorPath`); the legacy alternation exists so
// old deep links still resolve to the same editor.
const FILE_PATH_RE = /^\/(?:files|f)\/([A-Za-z0-9_-]+)\/?$/;

/** The stable editor path for a cloud file. */
export const fileEditorPath = (id: string) => `/files/${id}`;

/**
 * Return the cloud file id if the given pathname (default: current location) is
 * a `/files/<id>` editor URL (or the legacy `/f/<id>` shape), otherwise null.
 */
export const getCloudFileIdFromPath = (
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "",
): string | null => {
  const match = pathname.match(FILE_PATH_RE);
  return match ? match[1] : null;
};

/** An in-app editor route: the scratch canvas, or a per-file editor URL. */
const isEditorPath = (pathname: string) =>
  pathname === "/" || getCloudFileIdFromPath(pathname) !== null;

/**
 * Where the ← button on `/files` should go.
 *
 * "Back to canvas" always meant `/`, so returning from a file dropped the user
 * on the scratch canvas instead of the file they came from. Prefer real history
 * whenever the previous entry is an in-app editor route: within one document
 * that is what a router history entry means (`hasRouterHistory`); across a full
 * page load (☰ → My Files assigns `location.href`) the referrer IS the previous
 * entry. Anything else — a deep link, an external referrer — has no in-app
 * "back", so fall through to the canvas.
 */
export const resolveFilesBackTarget = ({
  hasRouterHistory,
  referrer,
  origin,
}: {
  hasRouterHistory: boolean;
  referrer: string;
  origin: string;
}): "back" | "/" => {
  if (hasRouterHistory) {
    return "back";
  }
  if (!referrer) {
    return "/";
  }
  try {
    const previous = new URL(referrer);
    return previous.origin === origin && isEditorPath(previous.pathname)
      ? "back"
      : "/";
  } catch {
    return "/";
  }
};

/**
 * "New mindmap" from My Files is a cross-document handoff (the two pages don't
 * share state), so the request travels in the URL: `/?new=1` asks the editor to
 * run the same New-mindmap flow as ☰ → New mindmap. The editor scrubs the
 * marker once honoured, so a reload or Back can't silently reset the canvas.
 */
const NEW_MINDMAP_PARAM = "new";

/** The canvas URL that requests a new mindmap on arrival. */
export const newMindmapCanvasPath = () => `/?${NEW_MINDMAP_PARAM}=1`;

/** Does this query string carry the new-mindmap request? */
export const hasNewMindmapRequest = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
): boolean => new URLSearchParams(search).has(NEW_MINDMAP_PARAM);

// The collab room fragment. Single source of truth — `data/index.ts` re-exports
// the helpers below so route parsing lives in one (dependency-free) module.
const ROOM_HASH_RE = /^#room=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/;

/** Does this URL carry a well-formed `#room=<id>,<key>` fragment? */
export const isCollaborationLink = (link: string) =>
  ROOM_HASH_RE.test(new URL(link).hash);

/** The raw `#room=` match (roomId, roomKey), for callers that validate further. */
export const matchCollabRoomHash = (hash: string) => hash.match(ROOM_HASH_RE);

/**
 * The shareable invite URL for a session. Always origin-rooted: the room grants
 * no file access, so handing out `/files/<id>#room=…` would leak the owner's file
 * id and drop invitees on a path they can't read.
 */
export const getCollaborationLink = (
  data: { roomId: string; roomKey: string },
  origin: string = typeof window !== "undefined" ? window.location.origin : "",
) => `${origin}/#room=${data.roomId},${data.roomKey}`;

/**
 * The cloud file the editor should actually open, or null. A `#room=` URL is a
 * pure session, so the whole cloud subsystem (hydration, 404, autosave) stays
 * dormant even on a `/files/<id>` path — that is what makes an already-shared
 * `/files/<id>#room=…` link work for invitees. A malformed `#room` fragment is
 * NOT a session and deliberately falls through to normal cloud-file behaviour.
 */
export const getCloudFileIdFromUrl = (
  url: string = typeof window !== "undefined" ? window.location.href : "",
): string | null => {
  if (!url) {
    return null;
  }
  const parsed = new URL(url);
  return isCollaborationLink(url)
    ? null
    : getCloudFileIdFromPath(parsed.pathname);
};

/**
 * When a session ends, should the URL fall back to the scratch canvas? A
 * room-only participant sits on a `/files/<id>` URL they cannot read, so leaving
 * them there would 404; the owner (their cloud file is live and autosaving) must
 * stay put.
 */
export const shouldResetUrlOnSessionEnd = ({
  pathname,
  isCloudFileOpen,
}: {
  pathname: string;
  isCloudFileOpen: boolean;
}): boolean => !isCloudFileOpen && getCloudFileIdFromPath(pathname) !== null;
